/* eslint-disable unicorn/prevent-abbreviations */
/* eslint-disable unicorn/no-object-as-default-parameter */
/* eslint-disable unicorn/no-useless-undefined */
/* eslint-disable unicorn/no-array-for-each */

import Axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios'

import { Str } from '../Str'
import type {
  ClientHeaders,
  ClientHttpMethod,
  StatelessFileClientFormdata,
  StatelessFileClientFormdataResponse,
  StatelessFileClientOptions,
  StatelessFileClientResponse,
  StatelessFileClientUploadForm,
} from '.'
import { Client } from './Client'
import { RpcClient } from './RpcClient'

type UploadStep = 'create_form' | 'upload' | 'extract'

export type ProgressEvent = (progress: number) => void

export class StatelessFileClient extends Client {
  private progressSteps: { type: UploadStep; value: number }[] = [
    { type: 'create_form', value: 0.3 },
    { type: 'upload', value: 0.7 },
  ]

  /**
   * @param options You must provide an RpcClient!
   */
  constructor(
    options: StatelessFileClientOptions = {
      rpcClient: new RpcClient(),
    },
  ) {
    super({
      ...options,
      headers: {
        Accept: 'application/xml',
      },
      errorResolver: (error) => {
        const logPrefix = '[NETWORK-RESPONSE]'
        Client.debug(`${logPrefix} ${error}`)
        return Promise.reject(error)
      },
      responseResolver: (response) => {
        const logPrefix = '[NETWORK-RESPONSE]'
        Client.debug(`${logPrefix} ${response}`)
        return Promise.resolve(undefined)
      },
    })
  }

  /**
   * @example
   * StatelessFileClient.upload(file);
   */
  async upload(file: File, onProgress: ProgressEvent): Promise<StatelessFileClientResponse> {
    const { key, disk } = await this.getUploadForm(file, onProgress)
    return { key: `stateless-upload://${disk}/${key}`, file }
  }

  protected async getUploadForm(file: File, onProgress: ProgressEvent): Promise<StatelessFileClientUploadForm> {
    const payload = { config: { mime: file.type } }

    this.updateProgress('create_form', 0, onProgress)
    const formdataResponse = (await (this.$options as StatelessFileClientOptions).rpcClient.execute('stateless-uploads:form:create', payload, {
      onUploadProgress: (event) => this.updateProgress('create_form', event.progress, onProgress),
    })) as StatelessFileClientFormdataResponse
    this.updateProgress('create_form', 1, onProgress)

    if (!formdataResponse.formdata) {
      throw new Error('Formdata response missing.')
    }

    const response = await this.uploadImageToS3(formdataResponse.formdata, file, onProgress)
    this.updateProgress('upload', 1, onProgress)

    return this.extractS3Key(response, formdataResponse.disk)
  }

  protected buildUploadPayload(formdata: StatelessFileClientFormdata, file: File): FormData {
    const payload = new FormData()
    Object.keys(formdata.inputs).forEach((key: string) => {
      const value = formdata.inputs[`${key}`]

      if (value) {
        payload.append(key, value)
      }
    })

    payload.append('Content-Type', file.type)
    payload.append('file', new File([file], Str.sanitizeFilename(file.name)))

    return payload
  }

  protected async uploadImageToS3(formdata: StatelessFileClientFormdata, file: File, onProgress: ProgressEvent): Promise<AxiosResponse> {
    this.updateProgress('upload', 0, onProgress)
    return Axios.create().request({
      url: formdata.attributes.action,
      method: formdata.attributes.method,
      data: this.buildUploadPayload(formdata, file),
      onUploadProgress: (event) => this.updateProgress('upload', event.progress, onProgress),
    })
  }

  protected extractS3Key(response: AxiosResponse, disk: string): StatelessFileClientUploadForm {
    const responseText = response.data

    const match = responseText.match(/<Key>(.+?)<\/Key>/)

    if (match && match[1]) {
      return { key: match[1], disk }
    }

    throw new Error(`Failed to extract s3 key from: ${responseText}`)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  call(_method: ClientHttpMethod, _url: string, _payload: any, _headers: ClientHeaders = {}): Promise<any> {
    throw new Error('Please use the upload method when using the StatelessFileClient')
  }

  private updateProgress(stepType: UploadStep, progress: number | undefined, onProgress: ProgressEvent) {
    let sum = 0
    for (const step of this.progressSteps) {
      if (stepType === step.type) {
        const totalProgress = sum + step.value * (progress || 0.5)
        onProgress(totalProgress)
        return totalProgress
      }
      sum += step.value
    }
  }
}
